import IDate from "@dvrd/idate";
import React, {useState} from 'react';
import {RegisterErrors} from "../../../util/types";
import {sendRegisterRequest} from "../../../util/requestSender";
import {ISO_DATE_FORMAT} from "../../../util/constants";
import {nullify, showDialog, showSnackbar, validateEmail} from "@dvrd/dvr-controls";
import {ResponseData} from "@dvrd/fetch";
import RegisterForm from "./registerForm";
import { createErrorMessage } from "../../../util/utils";

const emptyErrors: RegisterErrors = {
    address: null, city: null, dobOne: null, dobTwo: null, emailParentOne: null, nameOne: null, nameParentOne: null,
    nameTwo: null, telParentOne: null, zipCode: null
};

export default function RegisterFormController() {
    const [nameOne, setNameOne] = useState('');
    const [dobOne, setDobOne] = useState<IDate | null>(null);
    const [nameTwo, setNameTwo] = useState('');
    const [dobTwo, setDobTwo] = useState<IDate | null>(null);
    const [nameParentOne, setNameParentOne] = useState('');
    const [telParentOne, setTelParentOne] = useState('');
    const [emailParentOne, setEmailParentOne] = useState('');
    const [nameParentTwo, setNameParentTwo] = useState('');
    const [telParentTwo, setTelParentTwo] = useState('');
    const [emailParentTwo, setEmailParentTwo] = useState('');
    const [address, setAddress] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [city, setCity] = useState('');
    const [otherRegistrars, setOtherRegistrars] = useState('');
    const [errors, setErrors] = useState<RegisterErrors>(emptyErrors);
    const [loading, setLoading] = useState(false);

    function onChangeNameOne(value: string) {
        setNameOne(value);
        onResetError('nameOne')();
    }

    function onChangeDobOne(value: IDate | null) {
        setDobOne(value);
        onResetError('dobOne')();
    }

    function onChangeNameTwo(value: string) {
        setNameTwo(value);
        onResetError('nameTwo')();
    }

    function onChangeDobTwo(value: IDate | null) {
        setDobTwo(value);
        onResetError('dobTwo')();
    }

    function onChangeNameParentOne(value: string) {
        setNameParentOne(value);
        onResetError('nameParentOne')();
    }

    function onChangeTelParentOne(value: string) {
        setTelParentOne(value);
        onResetError('telParentOne')();
    }

    function onChangeEmailParentOne(value: string) {
        setEmailParentOne(value);
        onResetError('emailParentOne')();
    }

    function onChangeNameParentTwo(value: string) {
        setNameParentTwo(value);
    }

    function onChangeTelParentTwo(value: string) {
        setTelParentTwo(value);
    }

    function onChangeEmailParentTwo(value: string) {
        setEmailParentTwo(value);
    }

    function onChangeAddress(value: string) {
        setAddress(value);
        onResetError('address')();
    }

    function onChangeZipCode(value: string) {
        setZipCode(value);
        onResetError('zipCode')();
    }

    function onChangeCity(value: string) {
        setCity(value);
        onResetError('city')();
    }

    function onChangeOtherRegistrars(value: string) {
        setOtherRegistrars(value);
    }

    function onResetError(key: keyof RegisterErrors) {
        return function() {
            setErrors({...errors, [key]: null});
        }
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        sendRegisterRequest({
            data: {
                address, city, dob_one: dobOne!.format(ISO_DATE_FORMAT),
                dob_two: dobTwo?.format(ISO_DATE_FORMAT) ?? null, email_parent_one: emailParentOne,
                email_parent_two: nullify(emailParentTwo), name_one: nameOne, name_parent_one: nameParentOne,
                name_parent_two: nullify(nameParentTwo), name_two: nullify(nameTwo),
                other_registrars: nullify(otherRegistrars), tel_parent_one: telParentOne,
                tel_parent_two: nullify(telParentTwo), zip_code: zipCode
            }, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    showSnackbar('Uw aanmelding is ontvangen');
                    setNameOne('');
                    setDobOne(null);
                    setNameTwo('');
                    setDobTwo(null);
                    setNameParentOne('');
                    setTelParentOne('');
                    setEmailParentOne('');
                    setNameParentTwo('');
                    setTelParentTwo('');
                    setEmailParentTwo('');
                    setAddress('');
                    setZipCode('');
                    setCity('');
                    setOtherRegistrars('');
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het versturen van de aanmelding is niet gelukt.'),
                        'Versturen mislukt');
            }
        })
    }

    function validateValues(): boolean {
        let valid: boolean = true;
        const _errors = {...errors};
        const error = 'Dit is een verplicht veld';
        if (!nameOne.length) {
            _errors.nameOne = error;
            valid = false;
        }
        if (!dobOne) {
            _errors.dobOne = error;
            valid = false;
        }
        if (nameTwo.length && !dobTwo) {
            _errors.dobTwo = error;
            valid = false;
        }
        if (!nameTwo.length && !!dobTwo) {
            _errors.nameTwo = error;
            valid = false;
        }
        if (!nameParentOne.length) {
            _errors.nameParentOne = error;
            valid = false;
        }
        if (!telParentOne.length) {
            _errors.telParentOne = error;
            valid = false;
        }
        if (!emailParentOne.length) {
            _errors.emailParentOne = error;
            valid = false;
        } else if (!validateEmail(emailParentOne)) {
            _errors.emailParentOne = 'Vul een geldig e-mailadres in';
            valid = false;
        }
        if (!address.length) {
            _errors.address = error;
            valid = false;
        }
        if (!zipCode.length) {
            _errors.zipCode = error;
            valid = false;
        }
        if (!city.length) {
            _errors.city = error;
            valid = false;
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    return <RegisterForm onChangeNameOne={onChangeNameOne} onChangeDobOne={onChangeDobOne}
                         onChangeNameTwo={onChangeNameTwo} onChangeDobTwo={onChangeDobTwo}
                         onChangeNameParentOne={onChangeNameParentOne} onChangeTelParentOne={onChangeTelParentOne}
                         onChangeEmailParentOne={onChangeEmailParentOne} onChangeNameParentTwo={onChangeNameParentTwo}
                         onChangeTelParentTwo={onChangeTelParentTwo} onChangeEmailParentTwo={onChangeEmailParentTwo}
                         onChangeAddress={onChangeAddress} onChangeZipCode={onChangeZipCode} onChangeCity={onChangeCity}
                         onChangeOtherRegistrars={onChangeOtherRegistrars} onResetError={onResetError}
                         onSubmit={onSubmit} nameOne={nameOne} dobOne={dobOne} nameTwo={nameTwo} dobTwo={dobTwo}
                         nameParentOne={nameParentOne} telParentOne={telParentOne} emailParentOne={emailParentOne}
                         nameParentTwo={nameParentTwo} telParentTwo={telParentTwo} emailParentTwo={emailParentTwo}
                         address={address} zipCode={zipCode} city={city} otherRegistrars={otherRegistrars}
                         errors={errors} loading={loading}/>
}